import { cva, type VariantProps } from 'class-variance-authority'
import { Slot } from 'radix-ui'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Cog } from '~/components/brand'
import { cn } from '~/lib/utils'

/**
 * Minimum height is 44px on every size except `xs`, which is the smallest reliable
 * touch target. The audience uses this one-handed on a phone, often with gloves on.
 *
 * Buttons are deliberately *physical*: a top highlight, a shallow drop shadow, and a
 * one-pixel travel on press. On a touch screen with no hover state, that travel is
 * the main confirmation that the tap registered.
 */
const button = cva(
  'relative inline-flex cursor-pointer items-center justify-center gap-2 rounded-control font-semibold ' +
    'transition-[background-color,box-shadow,transform,color,border-color] duration-150 ' +
    'disabled:pointer-events-none disabled:opacity-50 ' +
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
  {
    variants: {
      variant: {
        primary:
          'bg-primary text-on-primary shadow-raise hover:bg-primary-hover ' +
          'active:translate-y-px active:shadow-inset',
        secondary:
          'border border-border-strong bg-surface text-ink shadow-card ' +
          'hover:border-ink-subtle hover:bg-surface-muted active:translate-y-px active:shadow-none',
        ghost: 'text-ink-muted hover:bg-surface-muted hover:text-ink active:translate-y-px',
        danger:
          'bg-danger text-white shadow-raise hover:brightness-110 ' +
          'active:translate-y-px active:shadow-inset',
        link: 'h-auto p-0 font-medium text-accent underline-offset-4 hover:underline',
      },
      size: {
        xs: 'h-8 px-2.5 text-xs',
        sm: 'h-10 px-3.5 text-sm',
        md: 'h-11 px-4 text-sm',
        lg: 'h-12 px-6 text-[0.9375rem]',
        icon: 'h-11 w-11',
      },
      full: { true: 'w-full' },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof button> {
  asChild?: boolean
  loading?: boolean
  icon?: ReactNode
}

export function Button({
  className,
  variant,
  size,
  full,
  asChild,
  loading,
  icon,
  children,
  disabled,
  ...props
}: ButtonProps) {
  // `asChild` hands the styling to a single child element — a `<Link>`, usually —
  // so the icon has to come from inside that child. Slot takes exactly one child,
  // and adding one here would break every link-shaped button at runtime rather
  // than at compile time.
  if (asChild) {
    return (
      <Slot.Root className={cn(button({ variant, size, full }), className)} {...props}>
        {children}
      </Slot.Root>
    )
  }

  return (
    <button
      className={cn(button({ variant, size, full }), className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? <Cog className="size-4" /> : icon}
      {children}
    </button>
  )
}
